import { useMemo, useEffect } from "react";
import Link from "next/link";
import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";

import { useSelector } from "react-redux";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useStoreContext } from "@components/store/provider";
import { useMenuAnalytics } from "@hooks/analytics/use-menu-analytics";

import SliderDropdown from "@stadium/ui/src/components/SliderDropdown";
import EditCategories from "@components/store/multi-level-menu/edit-categories";

import { getMenu, getMenuCodeMapping } from "@selectors/menu";

import { checkIfMenuExists, getParentCategoryFromCategory } from "@helpers/menu";
import { getResponsiveModifiedMenuList } from "@components/store/multi-level-menu/serializer";

import { ALL_SUBCATEGORY_CODE, getMenuItemRoute } from "@constants/routes";

import { DEFAULT_MENU_ITEM_CODE } from "@constants/menu";
import styles from "./main.module.scss";

const MultiLevelMenu = () => {

  const { trackMainCategory, trackSubcategory } = useMenuAnalytics();

  const {
    storeBasePath,
    persistedFiltersQuery,
    isPreviewCatalogMode,
    category,
    subCategory,
    countryCode,
  } = useStoreContext();
  const orderedMenuItem = useSelector(getMenu);
  const menuCodeMapping = useSelector(getMenuCodeMapping);

  const router = useRouter();

  const { parentCategoryCode, activeCategoryCode } = getParentCategoryFromCategory(category);

  const activeLevelCategory = {
    1: parentCategoryCode === DEFAULT_MENU_ITEM_CODE ? activeCategoryCode : parentCategoryCode,
    2: parentCategoryCode === DEFAULT_MENU_ITEM_CODE ? subCategory : activeCategoryCode,
    3: subCategory,
  };

  const menuList = orderedMenuItem?.children || [];
  const isMultiLevelMenu = orderedMenuItem?.totalLevel > 2;
  const skipTitleHumanization = orderedMenuItem?.skipTitleHumanization;

  const finalList = useMemo(
    () =>
      getResponsiveModifiedMenuList({
        list: orderedMenuItem?.children,
        parentItemCode: "",
        level: 1,
        totalLevel: orderedMenuItem?.totalLevel,
        isPreviewCatalogMode,
        isMultiLevelMenu,
      }),
    [orderedMenuItem],
  );

  useEffect(() => {
    if (
      !isEmpty(menuCodeMapping) &&
      activeCategoryCode &&
      subCategory &&
      !checkIfMenuExists(
        menuCodeMapping[parentCategoryCode]?.children,
        activeCategoryCode,
        subCategory,
      )
    ) {
      router.replace(
        getMenuItemRoute({
          storeBasePath,
          category: menuList[0].code,
          query: persistedFiltersQuery,
        }),
      );
    }
  }, [
    countryCode,
    menuList,
    category,
    subCategory,
    menuCodeMapping,
  ]);

  const renderBackLabel = (menuItem) => {
    return (
      <div className={styles.navLink} title={""}>
        <span className={skipTitleHumanization ? "" : styles.textTransform}>
          {skipTitleHumanization ? menuItem.name : capitalize(menuItem.name)}
        </span>
      </div>
    );
  };

  const renderLabel = (menuItem, className = "", { currentLevel, parentItem } = {}) => {
    const category =
      menuItem.itemCode === ALL_SUBCATEGORY_CODE ? menuItem.parentItemCode : menuItem.itemCode;
    return (
      <Link
        passHref
        href={getMenuItemRoute({
          storeBasePath,
          category,
          query: persistedFiltersQuery,
        })}
        className={`${styles.navLink} ${className}`}
        title={""}
        prefetch={false}
        onClick={() => {
          if (currentLevel === 1) {
            trackMainCategory(menuItem);
          } else if (parentItem) {
            trackSubcategory(parentItem, menuItem);
          }
        }}
      >
        <span
          className={`${styles.menuItemName} ${skipTitleHumanization ? "" : styles.textTransform}`}
        >
          {skipTitleHumanization ? menuItem.name : capitalize(menuItem.name)}
        </span>
      </Link>
    );
  };

  const renderNavLink = (menuList, currentLevel, isParentActive, parentItem) =>
    menuList.map((item) => {
      const isActive = isParentActive && item.code === activeLevelCategory[currentLevel];
      const activeClassName = isActive ? styles.activeList : "";

      return (
        <SliderDropdown
          styles={styles}
          label={renderLabel(item, activeClassName, { currentLevel, parentItem })}
          backLabel={renderBackLabel(item)}
          level={currentLevel}
          key={item.code}
          allowFullWidthClick={false}
        >
          {renderMenu(item.children, currentLevel + 1, isActive, item)}
        </SliderDropdown>
      );
    });

  const renderMenu = (list = [], currentLevel = 1, isParentActive = true, parentItem = null) => {
    if (list.length === 0 || currentLevel > orderedMenuItem?.totalLevel) return "";

    return (
      <div className={styles.responsiveNavOuterContainer}>
        <ul className={styles.navContainer}>
          {renderNavLink(list, currentLevel, isParentActive, parentItem)}
          {currentLevel === 1 && <EditCategories />}
        </ul>
      </div>
    );
  };

  return renderMenu(finalList, 1, true, null);
};

export default MultiLevelMenu;
